import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { useLocale } from "./LocaleProvider"
import { useUserClients } from "../lib/queries"
import type { UserClient } from "../lib/api"

interface UserClientsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: number | null
  userName?: string
}

function UserClientsModal({ open, onOpenChange, userId, userName }: UserClientsModalProps) {
  const { t } = useLocale()
  const { data, isLoading } = useUserClients(open ? userId : null, open)
  const clients: UserClient[] = data?.clients ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{userName ?? data?.user.fullName ?? "—"}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t(
              "user_clients_summary",
              String(data?.clientCount ?? 0),
              String(data?.accountCount ?? 0),
            )}
          </p>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("loading")}</div>
        ) : clients.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("user_clients_empty")}
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((c) => (
              <div key={c.id} className="rounded-lg border">
                <div className="flex items-center justify-between border-b px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full border"
                      style={{
                        backgroundColor: c.color || "transparent",
                        borderColor: c.color || "hsl(var(--border))",
                      }}
                    />
                    <span className="text-sm font-medium">{c.fullName}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {c.accounts.length} {t("nav_accounts").toLowerCase()}
                  </span>
                </div>
                {c.accounts.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">—</div>
                ) : (
                  <div>
                    {c.accounts.map((a) => (
                      <div
                        key={a.id}
                        className="grid grid-cols-[1fr_1.4fr_auto_auto] items-center gap-3 border-b px-4 py-2 text-sm last:border-b-0"
                      >
                        <span className="font-mono truncate">{a.accountId}</span>
                        <span className="truncate text-muted-foreground">{a.email || "—"}</span>
                        <span className="text-muted-foreground">
                          {a.status ? a.status.replace(/_/g, " ") : "—"}
                        </span>
                        <span className="w-24 text-right text-muted-foreground">
                          {a.strategy ? a.strategy.replace(/_/g, " ") : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default UserClientsModal
